import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { PageHeader } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import { Check, Eye, X, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Company", "Services", "Credentials", "Case Studies", "Team"];
const CATS = ["IT Services", "Logistics", "Marketing", "Legal", "HR", "Finance", "Manufacturing"];
const SIZES = ["1-50", "51-200", "201-1000", "1000+"];

export default function VendorProfileEditor() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [v, setV] = useState(null);
  const [tagInput, setTagInput] = useState("");
  const [coverageInput, setCoverageInput] = useState("");

  useEffect(() => { api.get("/vendors/me").then((r) => setV(r.data)).catch(() => setV({})); }, []);

  if (!v) return <Skeleton className="h-96" />;

  const update = (patch) => setV((prev) => ({ ...prev, ...patch }));

  const save = async () => {
    const payload = {
      name: v.name,
      tagline: v.tagline,
      founded_year: v.founded_year,
      company_size: v.company_size,
      location: v.location,
      website: v.website,
      logo: v.logo,
      category: v.category,
      description: v.description,
      service_tags: v.service_tags,
      geographic_coverage: v.geographic_coverage,
      certifications: v.certifications,
      iso_standards: v.iso_standards,
      govt_registrations: v.govt_registrations,
      case_studies: v.case_studies,
      team: v.team,
      industries: v.industries,
    };
    try {
      const { data } = await api.put("/vendors/me", payload);
      if (data && data.id) setV(data);
      toast.success("Profile saved");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
  };

  const addTag = () => {
    if (tagInput.trim()) { update({ service_tags: [...(v.service_tags || []), tagInput.trim()] }); setTagInput(""); }
  };
  const removeTag = (t) => update({ service_tags: (v.service_tags || []).filter((x) => x !== t) });

  const addCoverage = () => {
    if (coverageInput.trim()) { update({ geographic_coverage: [...(v.geographic_coverage || []), coverageInput.trim()] }); setCoverageInput(""); }
  };
  const removeCoverage = (c) => update({ geographic_coverage: (v.geographic_coverage || []).filter((x) => x !== c) });

  const addCert = () => update({ certifications: [...(v.certifications || []), { name: "", issuing_body: "", expiry_date: "", uploaded_at: new Date().toISOString() }] });
  const updateCert = (i, patch) => update({ certifications: v.certifications.map((c, idx) => idx === i ? { ...c, ...patch } : c) });
  const removeCert = (i) => update({ certifications: v.certifications.filter((_, idx) => idx !== i) });

  const addCase = () => update({ case_studies: [...(v.case_studies || []), { client_industry: "", challenge: "", solution: "", outcome_metric: "" }] });
  const updateCase = (i, patch) => update({ case_studies: v.case_studies.map((c, idx) => idx === i ? { ...c, ...patch } : c) });
  const removeCase = (i) => update({ case_studies: v.case_studies.filter((_, idx) => idx !== i) });

  const addTeam = () => update({ team: [...(v.team || []), { name: "", title: "", email: "", linkedin: "" }] });
  const updateTeam = (i, patch) => update({ team: v.team.map((m, idx) => idx === i ? { ...m, ...patch } : m) });
  const removeTeam = (i) => update({ team: v.team.filter((_, idx) => idx !== i) });

  return (
    <div className="animate-fade-in">
      <PageHeader
        title="Edit Profile"
        subtitle="Showcase your company to enterprise buyers"
        actions={[
          <Button key="preview" variant="outline" onClick={() => {
            if (v.id) navigate(`/vendor/${v.id}`);
            else toast.error("Save your profile first to preview");
          }} data-testid="preview-btn"><Eye className="w-4 h-4 mr-1.5" /> Preview</Button>,
          <Button key="save" onClick={save} className="bg-[#6366F1] hover:bg-[#4F46E5]" data-testid="save-btn">Save</Button>,
        ]}
      />

      <div className="grid grid-cols-1 lg:grid-cols-[220px,1fr] gap-6">
        <div className="bg-white border border-zinc-200 rounded-md p-2 h-fit">
          {STEPS.map((s, i) => (
            <button key={s} onClick={() => setStep(i)} className={`w-full text-left px-4 py-2.5 rounded-md text-sm font-medium ${step === i ? "bg-[#6366F1] text-white" : "hover:bg-zinc-50"}`} data-testid={`step-${i}`}>
              <span className="text-xs mr-2 opacity-60">{i + 1}.</span>{s}
            </button>
          ))}
        </div>

        <div className="bg-white border border-zinc-200 rounded-md p-6 space-y-4">
          {step === 0 && (
            <>
              <h3 className="font-semibold text-lg">Company information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Company name</Label><Input value={v.name || ""} onChange={(e) => update({ name: e.target.value })} className="mt-1.5" data-testid="vp-name" /></div>
                <div><Label>Tagline</Label><Input value={v.tagline || ""} onChange={(e) => update({ tagline: e.target.value })} className="mt-1.5" /></div>
                <div><Label>Founded year</Label><Input type="number" value={v.founded_year || ""} onChange={(e) => update({ founded_year: parseInt(e.target.value) || 0 })} className="mt-1.5" /></div>
                <div><Label>Company size</Label>
                  <Select value={v.company_size || "1-50"} onValueChange={(val) => update({ company_size: val })}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>{SIZES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>HQ location</Label><Input value={v.location || ""} onChange={(e) => update({ location: e.target.value })} className="mt-1.5" placeholder="e.g. Bangalore, Karnataka" /></div>
                <div><Label>Website</Label><Input value={v.website || ""} onChange={(e) => update({ website: e.target.value })} className="mt-1.5" /></div>
                <div className="md:col-span-2"><Label>Logo URL</Label><Input value={v.logo || ""} onChange={(e) => update({ logo: e.target.value })} className="mt-1.5" placeholder="Paste a logo image URL" /></div>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <h3 className="font-semibold text-lg">Services & coverage</h3>
              <div><Label>Primary category</Label>
                <Select value={v.category || "IT Services"} onValueChange={(val) => update({ category: val })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Service tags</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addTag())} placeholder="Add tag and press Enter" />
                  <Button onClick={addTag}><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {(v.service_tags || []).map((t) => (
                    <Badge key={t} variant="secondary" className="gap-1">{t}<button onClick={() => removeTag(t)}><X className="w-3 h-3" /></button></Badge>
                  ))}
                </div>
              </div>
              <div><Label>Description</Label><Textarea value={v.description || ""} onChange={(e) => update({ description: e.target.value })} rows={4} className="mt-1.5" /></div>
              <div><Label>Geographic coverage</Label>
                <div className="flex gap-2 mt-1.5">
                  <Input value={coverageInput} onChange={(e) => setCoverageInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addCoverage())} placeholder="Add region and press Enter" />
                  <Button onClick={addCoverage}><Plus className="w-4 h-4" /></Button>
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {(v.geographic_coverage || []).map((c) => (
                    <Badge key={c} variant="outline" className="gap-1">{c}<button onClick={() => removeCoverage(c)}><X className="w-3 h-3" /></button></Badge>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Credentials</h3>
                <Button size="sm" onClick={addCert}><Plus className="w-3.5 h-3.5 mr-1" /> Add certification</Button>
              </div>
              <div className="space-y-3">
                {(v.certifications || []).map((c, i) => (
                  <div key={i} className="border border-zinc-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-3 gap-3 relative">
                    <Input placeholder="Certificate name" value={c.name} onChange={(e) => updateCert(i, { name: e.target.value })} />
                    <Input placeholder="Issuing body" value={c.issuing_body} onChange={(e) => updateCert(i, { issuing_body: e.target.value })} />
                    <Input type="date" value={c.expiry_date?.slice(0, 10) || ""} onChange={(e) => updateCert(i, { expiry_date: new Date(e.target.value).toISOString() })} />
                    <button onClick={() => removeCert(i)} className="absolute top-2 right-2 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <div className="pt-4 border-t">
                <h4 className="font-semibold text-sm mb-3">Government registrations</h4>
                <div className="space-y-2">
                  {["msme", "startup_india", "dpiit"].map((k) => (
                    <div key={k} className="flex items-center justify-between p-2 border rounded-md">
                      <span className="text-sm">{k === "msme" ? "MSME" : k === "startup_india" ? "Startup India" : "DPIIT"}</span>
                      <Switch checked={!!v.govt_registrations?.[k]} onCheckedChange={(val) => update({ govt_registrations: { ...v.govt_registrations, [k]: val } })} />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Case studies</h3>
                <Button size="sm" onClick={addCase} disabled={(v.case_studies || []).length >= 5}><Plus className="w-3.5 h-3.5 mr-1" /> Add case study</Button>
              </div>
              <div className="space-y-3">
                {(v.case_studies || []).map((c, i) => (
                  <div key={i} className="border border-zinc-200 rounded-md p-4 space-y-2 relative">
                    <Input placeholder="Client industry" value={c.client_industry} onChange={(e) => updateCase(i, { client_industry: e.target.value })} />
                    <Textarea placeholder="Challenge" value={c.challenge} onChange={(e) => updateCase(i, { challenge: e.target.value })} rows={2} />
                    <Textarea placeholder="Solution" value={c.solution} onChange={(e) => updateCase(i, { solution: e.target.value })} rows={2} />
                    <Input placeholder="Outcome metric (e.g., 42% cost saving)" value={c.outcome_metric} onChange={(e) => updateCase(i, { outcome_metric: e.target.value })} />
                    <button onClick={() => removeCase(i)} className="absolute top-2 right-2 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Team contacts</h3>
                <Button size="sm" onClick={addTeam}><Plus className="w-3.5 h-3.5 mr-1" /> Add member</Button>
              </div>
              <div className="space-y-3">
                {(v.team || []).map((m, i) => (
                  <div key={i} className="border border-zinc-200 rounded-md p-4 grid grid-cols-1 md:grid-cols-2 gap-3 relative">
                    <Input placeholder="Full name" value={m.name} onChange={(e) => updateTeam(i, { name: e.target.value })} />
                    <Input placeholder="Title" value={m.title} onChange={(e) => updateTeam(i, { title: e.target.value })} />
                    <Input placeholder="Email" value={m.email} onChange={(e) => updateTeam(i, { email: e.target.value })} />
                    <Input placeholder="LinkedIn URL" value={m.linkedin} onChange={(e) => updateTeam(i, { linkedin: e.target.value })} />
                    <button onClick={() => removeTeam(i)} className="absolute top-2 right-2 text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            </>
          )}

          <div className="flex justify-between pt-4 border-t">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)}>Previous</Button>
            {step < STEPS.length - 1 ? (
              <Button onClick={() => setStep(step + 1)} className="bg-[#6366F1] hover:bg-[#4F46E5]" data-testid="next-step">Next</Button>
            ) : (
              <Button onClick={save} className="bg-[#6366F1] hover:bg-[#4F46E5]"><Check className="w-4 h-4 mr-1.5" /> Save & Finish</Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
